import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'sonner';
import ReactDOM from 'react-dom';
import { Users, Search, Edit3, Trash2, X, Check } from 'lucide-react';

const ALL_ROLES = ['member', 'consultant', 'dr', 'dn', 'direction', 'support'];
const ROLE_LABELS = {
  member: { fr: 'Membre', en: 'Member' },
  consultant: { fr: 'Consultant', en: 'Consultant' },
  dr: { fr: 'DR (Directeur Régional)', en: 'DR (Regional Director)' },
  dn: { fr: 'DN (Directeur National)', en: 'DN (National Director)' },
  direction: { fr: 'Direction APOLLO', en: 'Direction APOLLO' },
  support: { fr: 'Support APOLLO', en: 'Support APOLLO' },
};
const ROLE_COLORS = {
  member: '#3B82F6', consultant: '#8B5CF6', dr: '#F77F00',
  dn: '#EC4899', direction: '#EF4444', support: '#22C55E',
};

export default function UserManagementPage() {
  const { lang } = useLang();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [editingUser, setEditingUser] = useState(null);   // user object when editing
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [deleteTarget, setDeleteTarget] = useState(null); // user object for delete confirmation
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Escape key + body scroll lock pour le modal édition mobile (portal) — aligné sur ConfirmDialog
  useEffect(() => {
    if (!editingUser || !isMobile) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) cancelEdit(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [editingUser, isMobile, saving]);

  function fetchUsers() {
    api.get('/admin/users').then(res => setUsers(res.data)).catch(() => {}).finally(() => setLoading(false));
  }

  function startEdit(u) {
    setEditingUser(u);
    setEditData({ name: u.name, role: u.role, group_id: u.group_id || '', region_id: u.region_id || '' });
  }

  function cancelEdit() { setEditingUser(null); setEditData({}); }

  function saveEdit() {
    if (!editingUser) return;
    const payload = {};
    if (editData.role) payload.role = editData.role;
    if (editData.group_id !== undefined) payload.group_id = editData.group_id || null;
    if (editData.region_id !== undefined) payload.region_id = editData.region_id || null;
    if (editData.name) payload.name = editData.name;
    setSaving(true);
    api.put('/admin/users/' + editingUser._id, payload)
      .then(() => { toast.success(lang === 'fr' ? 'Utilisateur mis à jour' : 'User updated'); cancelEdit(); fetchUsers(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Error'))
      .finally(() => setSaving(false));
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    api.delete('/admin/users/' + deleteTarget._id)
      .then(() => { toast.success(lang === 'fr' ? 'Utilisateur supprimé' : 'User deleted'); setDeleteTarget(null); fetchUsers(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Error'))
      .finally(() => setDeleting(false));
  }

  const filtered = users.filter(u => {
    if (filterRole && u.role !== filterRole) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
    }
    return true;
  });

  const roleCounts = {};
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  const editForm = editingUser ? (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Nom' : 'Name'}</label>
          <input data-testid="edit-name" value={editData.name || ''} onChange={e => setEditData({ ...editData, name: e.target.value })} className="apollo-input w-full" style={{ minHeight: 44 }} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#9CA3AF' }}>{t('role', lang)}</label>
          <select data-testid="edit-role" value={editData.role || ''} onChange={e => setEditData({ ...editData, role: e.target.value })} className="apollo-input w-full" style={{ minHeight: 44, background: '#0F0F1A', cursor: 'pointer' }}>
            {ALL_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r][lang]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Groupe' : 'Group'}</label>
          <input data-testid="edit-group" value={editData.group_id || ''} onChange={e => setEditData({ ...editData, group_id: e.target.value })} className="apollo-input w-full" placeholder="Group ID" style={{ minHeight: 44 }} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Région' : 'Region'}</label>
          <input data-testid="edit-region" value={editData.region_id || ''} onChange={e => setEditData({ ...editData, region_id: e.target.value })} className="apollo-input w-full" placeholder="Region ID" style={{ minHeight: 44 }} />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button data-testid="save-edit" onClick={saveEdit} disabled={saving} className="flex items-center justify-center gap-1 px-4 rounded-lg text-sm font-semibold" style={{ background: '#22C55E', color: '#fff', minHeight: 44, opacity: saving ? 0.5 : 1 }}>
          <Check size={14} /> {t('save', lang)}
        </button>
        <button data-testid="cancel-edit" onClick={cancelEdit} disabled={saving} className="flex items-center justify-center gap-1 px-4 rounded-lg text-sm font-semibold" style={{ background: 'rgba(107,114,128,0.3)', color: '#E8E8E8', minHeight: 44 }}>
          <X size={14} /> {t('cancel', lang)}
        </button>
      </div>
    </div>
  ) : null;

  // Modal portal for mobile edit (< lg)
  const editModal = editingUser && isMobile ? (
    <div
      data-testid="edit-modal-backdrop"
      onClick={() => !saving && cancelEdit()}
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        data-testid="edit-modal"
        onClick={e => e.stopPropagation()}
        className="animate-fade-in"
        style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 className="text-base font-bold text-white truncate">{lang === 'fr' ? 'Modifier ' : 'Edit '}{editingUser.name}</h3>
          <button data-testid="edit-modal-close" onClick={cancelEdit} aria-label="Fermer" className="p-1 rounded-lg hover:bg-white/5 flex-shrink-0" style={{ color: '#9CA3AF' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {editForm}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <Layout>
      <div className="animate-fade-in">
        <div className="flex items-center gap-2 sm:gap-3 mb-5 sm:mb-6">
          <Users size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
          <h1 data-testid="user-management-title" className="text-xl sm:text-2xl font-bold text-white truncate">{lang === 'fr' ? 'Gestion des Utilisateurs' : 'User Management'}</h1>
          <span className="text-xs sm:text-sm px-2.5 py-0.5 rounded-full flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)', color: '#F77F00' }}>{users.length}</span>
        </div>

        {/* Role stats - 2 cols mobile, 3 cols sm, 6 cols lg */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-5 sm:mb-6">
          {ALL_ROLES.map(role => {
            const count = roleCounts[role] || 0;
            const color = ROLE_COLORS[role];
            const active = filterRole === role;
            return (
              <button key={role} data-testid={'role-filter-' + role}
                onClick={() => setFilterRole(active ? '' : role)}
                className="apollo-card p-3 text-center transition-all cursor-pointer"
                style={{ borderColor: active ? color : 'rgba(255,255,255,0.08)', borderWidth: active ? '2px' : '1px', minHeight: 60 }}>
                <p className="text-xl sm:text-2xl font-bold" style={{ color }}>{count}</p>
                <p className="text-[10px] sm:text-xs mt-0.5 sm:mt-1 truncate" style={{ color: '#9CA3AF' }}>{ROLE_LABELS[role][lang]}</p>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
          <div className="relative flex-1 sm:max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#6B7280' }} />
            <input data-testid="user-search" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="apollo-input w-full" placeholder={t('search', lang)} style={{ minHeight: 44, paddingLeft: 40 }} />
          </div>
          {filterRole && (
            <button onClick={() => setFilterRole('')} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0" style={{ background: 'rgba(247,127,0,0.1)', color: '#F77F00', minHeight: 40 }}>
              {ROLE_LABELS[filterRole][lang]} <X size={12} />
            </button>
          )}
        </div>

        {/* User list */}
        {loading ? (
          <p className="text-center py-12" style={{ color: '#6B7280' }}>{t('loading', lang)}</p>
        ) : (
          <div className="space-y-2">
            {filtered.map(u => {
              const isEditingDesktopInline = editingUser && editingUser._id === u._id && !isMobile;
              const color = ROLE_COLORS[u.role] || '#6B7280';
              return (
                <div key={u._id} data-testid={'user-row-' + u._id} className="apollo-card p-3 sm:p-4 animate-fade-in">
                  {isEditingDesktopInline ? (
                    editForm
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: color + '20' }}>
                          <span style={{ color, fontWeight: 600, fontSize: 14 }}>{u.name ? u.name.charAt(0) : '?'}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-semibold text-white text-sm sm:text-base truncate max-w-full">{u.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: color + '20', color }}>{ROLE_LABELS[u.role] ? ROLE_LABELS[u.role][lang] : u.role}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 text-xs flex-wrap" style={{ color: '#6B7280' }}>
                            <span className="truncate max-w-full">{u.email}</span>
                            {u.group_name ? <span className="truncate">Groupe: {u.group_name}</span> : null}
                            {u.region_name ? <span className="truncate">Région: {u.region_name}</span> : null}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 sm:justify-end justify-end pt-2 sm:pt-0 border-t sm:border-t-0 -mx-3 sm:mx-0 px-3 sm:px-0" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                        <button data-testid={'edit-user-' + u._id} onClick={() => startEdit(u)} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#F77F00', width: 44, height: 44 }} aria-label={lang === 'fr' ? 'Modifier' : 'Edit'}>
                          <Edit3 size={16} />
                        </button>
                        <button data-testid={'delete-user-' + u._id} onClick={() => setDeleteTarget(u)} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#EF4444', width: 44, height: 44 }} aria-label={lang === 'fr' ? 'Supprimer' : 'Delete'}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal édition mobile via portal */}
      {editModal && typeof document !== 'undefined' ? ReactDOM.createPortal(editModal, document.body) : null}

      {/* Confirmation de suppression défensive */}
      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={lang === 'fr' ? 'Supprimer cet utilisateur ?' : 'Delete this user?'}
        message={deleteTarget ? (lang === 'fr'
          ? `Cette action supprimera définitivement ${deleteTarget.name} (${deleteTarget.email}). Les tickets qu'il a créés ou auxquels il est assigné resteront visibles mais seront orphelins. Cette action est irréversible.`
          : `This will permanently delete ${deleteTarget.name} (${deleteTarget.email}). Tickets they created or are assigned to will remain visible but orphaned. This action cannot be undone.`) : ''}
        confirmLabel={lang === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
        cancelLabel={lang === 'fr' ? 'Annuler' : 'Cancel'}
        destructive
        submitting={deleting}
      />
    </Layout>
  );
}
